import { FormNumericInput, FormSelect } from "components/form-fields";
import { ListingTable } from "components/listing";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { Button } from 'components/buttons';
import { ShowHide } from "components/layout";
import { Tooltip } from "components/tooltip";
import { Icon } from "@blueprintjs/core";

const autoFrequencyTypeOptionList = [
    { id: "Days", name: "Days" },
    { id: "Months", name: "Months" },
    { id: "Years", name: "Years" },
];

const defaultRetentionPeriod = {
    value: 6,
    type: "month"
};

const defaultArray = [];

export function BaseRetentionPeriod({
    title,
    area,

    defaultPeriod = defaultRetentionPeriod,

    data = defaultArray,
    isLoading = false,
    isSaving = false,
    onActionClick,
    onAreaLevelChange,
    canBaselineRetentionPeriodNotBeEdited = false,
    ...rest
}) {
    const [period, setPeriod] = useState(defaultPeriod);

    const tableHeaders = ["Upload", "Period", "using Baseline", "Actions"];

    const canBaselineRetentionPeriodNotBeEditedValue = data.every(dr => dr.featureId) || rest.canBaselineRetentionPeriodNotBeEdited;

    const handleValueChange = (value) => {
        if (value !== period.value) {
            setPeriod((prev) => ({ ...prev, value }));
            onAreaLevelChange(area, { ...period, value });
        }
    };

    const tableData = useMemo(() => {
        return data.map(dr => {

            const isDocumentLevelRetentionEditable = !!dr.dataRetentionUnit && !dr.featureId;
            const useDefaultPeriod = !dr.dataRetentionPeriod || !dr.dataRetentionUnit;

            return ([
                dr.dataRetentionName,
                <ShowHide
                    key={dr.documentTypeId}
                    evaluator={useDefaultPeriod}
                    show={(
                        <span style={{ opacity: 0.5 }}>
                            {`${period.value} ${period.type}`}
                        </span>
                    )}
                    hide={(
                        <span style={{ opacity: isDocumentLevelRetentionEditable ? 1 : 0.5 }}>
                            {`${dr.dataRetentionPeriod} ${dr.dataRetentionUnit}`}
                        </span>
                    )}
                />,
                (useDefaultPeriod ? <Icon icon="tick" intent="success" /> : null),
                <div key={dr.documentTypeId} className="button-row-small">
                    <ShowHide
                        evaluator={isDocumentLevelRetentionEditable}
                        show={(
                            <Tooltip content={"Edit retention period"}>
                                <Button
                                    onClick={() => onActionClick?.({
                                        area,
                                        period: {
                                            type: dr.dataRetentionUnit,
                                            value: dr.dataRetentionPeriod
                                        },
                                        id: dr.documentTypeId,
                                        isUsingBaseline: false,
                                        dataRetentionName: dr.dataRetentionName,
                                        baselinePeriod: defaultPeriod
                                    })}
                                    icon="edit"
                                    large={false}
                                    minimal
                                    disabled={!!dr.featureId || isSaving}
                                />
                            </Tooltip>
                        )}
                        hide={(
                            <Tooltip content={dr.featureId ? "This is a feature enabled document type. The retention period cannot be overridden." : "Override baseline retention period"}>
                                <Button
                                    onClick={() => onActionClick?.({
                                        area,
                                        period: defaultPeriod,
                                        id: dr.documentTypeId,
                                        isUsingBaseline: true,
                                        dataRetentionName: dr.dataRetentionName,
                                        baselinePeriod: defaultPeriod
                                    })}
                                    icon="plus"
                                    large={false}
                                    minimal
                                    disabled={!!dr.featureId || isSaving}
                                />
                            </Tooltip>
                        )}
                    />
                </div>,
            ])
        })
    }, [data, period, isSaving, defaultPeriod]);

    return (
        <div className="spacer-top">
            <h4 className="bp6-heading spacer-top">{title}</h4>
            <div className="form-row field-combination spacer-bottom-small">
                <h4>Baseline data retention period</h4>
                <FormNumericInput
                    disabled={isLoading || canBaselineRetentionPeriodNotBeEditedValue || isSaving}
                    disabledInput={isLoading || canBaselineRetentionPeriodNotBeEditedValue || isSaving}
                    min={1}
                    max={1000}
                    selectedValue={period.value}
                    onValueChange={handleValueChange}
                    loading={isLoading}                
                />
                <FormSelect
                    disabled={isLoading || canBaselineRetentionPeriodNotBeEditedValue || isSaving}
                    items={autoFrequencyTypeOptionList}
                    selectedValue={period.type}
                    onItemSelect={(item) => {
                        if (item.id !== period.type) {
                            const updatedPeriod = { ...period, type: item.id };
                            setPeriod(updatedPeriod);
                            onAreaLevelChange(area, updatedPeriod);
                        }
                    }} loading={isLoading}
                />
                <ListingTable
                    id="listing-table-base-retention-period"
                    headers={tableHeaders}
                    data={tableData}
                    loadingData={isLoading}
                    totalRecordCount={tableData.length}
                    pageable={false}
                    shrinkLastColumn={true}
                    noDataMessage="There are no custom retention periods set."
                />
            </div>
        </div>
    );
}

BaseRetentionPeriod.propTypes = {
    title: PropTypes.string.isRequired,
    area: PropTypes.string.isRequired,
    data: PropTypes.array,
    defaultPeriod: PropTypes.shape({
        value: PropTypes.number,
        type: PropTypes.oneOf(
            autoFrequencyTypeOptionList.map(i => i.id)
        )
    }),
    isLoading: PropTypes.bool,
    onActionClick: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    canBaselineRetentionPeriodNotBeEdited: PropTypes.bool
};